import React, { useMemo, useState, useEffect } from "react";
import { Download, Filter, Search, Users } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import B2CHeader from "./B2CHeader";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const SelectBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const Badge = ({ tone = "slate", children }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    gray: "border-slate-200 bg-white text-slate-700",
  };

  return (
    <span className={cx("inline-flex items-center rounded-full border px-3 py-1 text-xs font-black", tones[tone] || tones.slate)}>
      {children}
    </span>
  );
};

// Mock data - replace with actual API call
const MOCK_CUSTOMERS = [
  {
    id: 1,
    name: "John Doe",
    email: "john@example.com",
    country: "USA",
    orderCount: 15,
    status: "active",
    joinDate: "2023-01-15",
    lastOrder: "2024-01-10",
    totalSpent: 1250.0,
  },
  {
    id: 2,
    name: "Jane Smith",
    email: "jane@example.com",
    country: "Canada",
    orderCount: 8,
    status: "active",
    joinDate: "2023-03-20",
    lastOrder: "2024-01-08",
    totalSpent: 890.5,
  },
  {
    id: 3,
    name: "Mike Johnson",
    email: "mike@example.com",
    country: "UK",
    orderCount: 3,
    status: "suspended",
    joinDate: "2023-06-10",
    lastOrder: "2023-12-15",
    totalSpent: 245.0,
  },
  {
    id: 4,
    name: "Sarah Wilson",
    email: "sarah@example.com",
    country: "Australia",
    orderCount: 0,
    status: "guest",
    joinDate: "2024-01-01",
    lastOrder: null,
    totalSpent: 0.0,
  },
];

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setCustomers(MOCK_CUSTOMERS);
      setLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    let filtered = customers;

    // Filter by search term
    if (customerFilter) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(customerFilter.toLowerCase()) ||
        customer.email.toLowerCase().includes(customerFilter.toLowerCase()) ||
        customer.country.toLowerCase().includes(customerFilter.toLowerCase()) ||
        customer.orderCount.toString().includes(customerFilter)
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(customer => customer.status === statusFilter);
    }

    setFilteredCustomers(filtered);
  }, [customers, customerFilter, statusFilter]);

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(customer => customer.id));
    }
  };

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on customers:`, selectedCustomers);
    // Implement bulk actions
  };

  const statusTone = (status) => {
    const map = {
      active: "green",
      suspended: "rose",
      guest: "gray",
    };
    return map[status] || "slate";
  };

  const selectedCount = selectedCustomers.length;
  const activeCount = useMemo(() => customers.filter((c) => c.status === "active").length, [customers]);
  const totalSpent = useMemo(
    () => customers.reduce((sum, c) => sum + (Number(c.totalSpent) || 0), 0),
    [customers]
  );

  return (
    <AdminPageShell
      title="Customers"
      description="Manage B2C customers, review activity, and control access. (Mock data)"
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge>
            <Users className="h-4 w-4" />
            {customers.length} total
          </Badge>
          <Badge tone="green">{activeCount} active</Badge>
          <Badge tone="gray">${totalSpent.toFixed(2)} spent</Badge>
        </div>
      }
      actions={
        <>
          <SecondaryButton type="button" onClick={() => {
            setCustomerFilter("");
            setStatusFilter("all");
            setSelectedCustomers([]);
          }}>
            Reset
          </SecondaryButton>
          <PrimaryButton type="button" onClick={() => alert("Export coming soon")}
          >
            <Download className="h-4 w-4" />
            Export
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-6">
        <B2CHeader />

        <Card className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Name, email, country, order count"
                  className={cx(FieldBase, "pl-10")}
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Status
              </label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <select
                  className={cx(SelectBase, "pl-10")}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="guest">Guest</option>
                </select>
              </div>
            </div>

            <div className="flex items-end">
              <PrimaryButton type="button" className="w-full justify-center" onClick={() => alert("Export coming soon")}
              >
                <Download className="h-4 w-4" />
                Export Customer Data
              </PrimaryButton>
            </div>
          </div>

          {selectedCount > 0 ? (
            <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">
                {selectedCount} selected
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <PrimaryButton type="button" className="px-4" onClick={() => handleBulkAction("activate")}>
                  Activate
                </PrimaryButton>
                <SecondaryButton type="button" className="px-4" onClick={() => handleBulkAction("suspend")}>
                  Suspend
                </SecondaryButton>
                <SecondaryButton type="button" className="px-4" onClick={() => handleBulkAction("export")}>
                  Export Selected
                </SecondaryButton>
              </div>
            </div>
          ) : null}
        </Card>

        <Card className="overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-black tracking-tight text-slate-900">Customers</p>
              <p className="text-sm text-slate-600">{filteredCustomers.length} shown</p>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                onChange={handleSelectAll}
                className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30"
              />
              Select all
            </label>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto" />
              <p className="mt-2 text-slate-600">Loading customers…</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Total Spent</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Last Order</th>
                    <th className="px-6 py-3 text-left text-xs font-black text-slate-600 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedCustomers.includes(customer.id)}
                              onChange={() => handleSelectCustomer(customer.id)}
                              className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30 mr-3"
                            />
                            <div>
                              <div className="text-sm font-semibold text-slate-900">
                                {customer.name}
                              </div>
                              <div className="text-sm text-slate-600">
                                Joined {new Date(customer.joinDate).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">{customer.email}</div>
                          <div className="text-sm text-slate-600">{customer.country}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {customer.orderCount} orders
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          ${customer.totalSpent.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge tone={statusTone(customer.status)}>
                            {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {customer.lastOrder ? new Date(customer.lastOrder).toLocaleDateString() : "No orders"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-wrap items-center gap-2">
                            <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => alert("View coming soon")}
                            >
                              View
                            </SecondaryButton>
                            <SecondaryButton type="button" className="px-3 py-1.5 text-xs" onClick={() => alert("Edit coming soon")}
                            >
                              Edit
                            </SecondaryButton>
                            <SecondaryButton
                              type="button"
                              className="px-3 py-1.5 text-xs border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                              onClick={() => alert("Suspend coming soon")}
                            >
                              Suspend
                            </SecondaryButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {filteredCustomers.length === 0 && !loading && (
              <div className="p-8 text-center">
                <p className="text-slate-600">No customers found matching your criteria.</p>
              </div>
            )}
        </Card>
      </div>
    </AdminPageShell>
  );
};

export default CustomerManagement;
